import { useEffect, useSyncExternalStore } from 'react';
import { useRouter } from 'next/router';
import { Capacitor } from '@capacitor/core';
import { getLogger } from './logger';
import {
    clearRestorePending,
    getRestorePending,
    isRestoringRoute,
    loadLastRoute,
    markRestorePending,
    planRestore,
    saveLastRoute,
    setRestoringRoute,
    subscribeRestoringRoute,
} from './lastRoute';
import { UserData } from './userdata';

const logger = getLogger('lastRoute');

// A cold launch starts a fresh JS context, so this module-scoped flag is false on every launch
// and ensures the restore runs at most once (not again on later in-session navigation to '/').
let restoreAttempted = false;

// Synchronous, conservative check (no I/O) for whether a restore is plausibly about to happen on
// this cold launch. We can't know the saved route without an async read, so this errs towards
// "yes" (cold launch + restore-capable + bare home URL) and the async decision later corrects it.
function mightRestoreSynchronously(): boolean {
    if (restoreAttempted || typeof window === 'undefined') {
        return false;
    }
    const restoreOnWeb = window.localStorage?.getItem('restoreOnWeb') === '1';
    if (!Capacitor.isNativePlatform() && !restoreOnWeb) {
        return false;
    }
    return window.location.pathname === '/' && !window.location.search;
}

// Raise the restoring flag at module evaluation (before React renders anything), so the home
// route can render a loader instead of mounting HomeAppShell during the async restore decision.
// On a no-restore launch the async decision clears it again, revealing the home page.
if (mightRestoreSynchronously()) {
    setRestoringRoute(true);
}

// Reactive view of the restoring flag for components that must hold a loader during a restore.
export function useIsRestoringRoute(): boolean {
    return useSyncExternalStore(subscribeRestoringRoute, isRestoringRoute, () => false);
}

// Backstop only: the watchdog is normally cleared as soon as the restored route's navigation
// completes (see below). This timer just guarantees the flags never get stuck set if that
// completion event is somehow missed.
const STABILIZE_MS = 5000;

function isPersistable(status: UserData['status']): boolean {
    return status === 'logged-in' || status === 'cached-data-available';
}

// Persists the route the user is on and, on a native cold launch, reopens the last route.
export function useRouteRestoration(userStatus: UserData['status']): void {
    const router = useRouter();
    const { asPath } = router;

    // Persist the current route as the user navigates — only while authenticated, so we never
    // store the login screen or a route reached mid-auth. sanitizeRoute drops transient/auth
    // routes and strips modal/redirect params.
    useEffect(() => {
        if (!isPersistable(userStatus)) {
            return;
        }
        saveLastRoute(asPath);
    }, [ asPath, userStatus ]);

    // Restore once, on a native cold launch that landed on the bare home route.
    useEffect(() => {
        if (restoreAttempted) {
            return;
        }
        restoreAttempted = true;
        // For manual web testing, set localStorage.restoreOnWeb = '1' in devtools, then
        // navigate to '/' to simulate a native cold launch (the web keeps its URL otherwise).
        const restoreOnWeb = typeof window !== 'undefined'
            && window.localStorage?.getItem('restoreOnWeb') === '1';

        (async () => {
            const [ pending, savedRoute ] = await Promise.all([ getRestorePending(), loadLastRoute() ]);
            const plan = planRestore({
                isNative: Capacitor.isNativePlatform(),
                restoreOnWeb,
                pathname: window.location.pathname,
                search: window.location.search,
                pending,
                savedRoute,
            });
            if (plan.action === 'none') {
                setRestoringRoute(false); // no restore — reveal the home page
                return;
            }
            if (plan.action === 'skip-crash-loop') {
                await clearRestorePending();
                setRestoringRoute(false); // not restoring this launch — reveal the home page
                logger.warn(`Skipping route restore; previous restore to '${plan.pending}' did not stabilize`);
                return;
            }
            logger.log(`Restoring last route '${plan.route}'`);
            await markRestorePending(plan.route);
            setRestoringRoute(true);

            // Treat the restore as successful — clearing the crash-loop watchdog and letting the
            // mismatch-recovery hack resume — as soon as the navigation to the restored route
            // completes (the app survived the restore). A blind STABILIZE_MS timer used to gate
            // this, but then a normal app kill within that window left the pending flag set, so
            // the next launch mistook a clean kill for a crash and skipped the restore (sending
            // the user Home). A genuine restore-induced native crash happens before the navigation
            // completes, so this still breaks a real crash loop. The timer remains only as a
            // backstop in case the completion event is missed.
            const targetPath = plan.route.split('?')[0];
            let settled = false;
            const settle = () => {
                if (settled) {
                    return;
                }
                settled = true;
                router.events.off('routeChangeComplete', handleRouteComplete);
                setRestoringRoute(false);
                clearRestorePending();
            };
            function handleRouteComplete(url: string) {
                if (url.split('?')[0] === targetPath) {
                    settle();
                }
            }
            router.events.on('routeChangeComplete', handleRouteComplete);
            router.replace(plan.route);
            setTimeout(settle, STABILIZE_MS);
        })();
    // Runs once per mount; intentionally not re-run when router/userStatus change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}
